/**
 * Sales route layout with tab strip + Outlet for the sub-views per
 * design README section 5. The Summary tab (route `/sales`) is the
 * default landing per the sales-led morning view convention; full
 * implementations land across M7 (Leads), M8 (Opportunities + Pipeline),
 * M9 (Summary queue), M10 (Stats).
 *
 * UAT Round 4 (PR #40) reshuffled the tab order. Summary leads,
 * followed by the day-to-day work surfaces (Leads, Opportunities),
 * then Pipeline (currently an under-construction placeholder while
 * Peter re-maps the layout), then Stats. The underlying routes did
 * not move; only the labels and order in the strip changed.
 *
 * Reference: spec sections 15.7–15.10.
 */

import { Link, Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sales')({
  component: SalesLayout,
});

/**
 * Ordered tab definitions for the Sales section strip. The default
 * landing route `/sales` is labelled SUMMARY (was QUEUE pre-PR #40).
 * Pipeline keeps its route but renders an under-construction
 * placeholder until the next round.
 */
const TABS = [
  { to: '/sales', label: 'SUMMARY' },
  { to: '/sales/leads', label: 'LEADS' },
  { to: '/sales/opportunities', label: 'OPPORTUNITIES' },
  { to: '/sales/pipeline', label: 'PIPELINE' },
  { to: '/sales/stats', label: 'STATS' },
] as const;

/**
 * Render the Sales tab strip used in the page header. Exported so the
 * component test can mount it inside a memory-history router without
 * booting the full application shell.
 */
export function TabStrip() {
  const { pathname } = useLocation();
  return (
    <nav className="flex gap-mx-4 border-b border-mx-grey-800" aria-label="Sales views">
      {TABS.map((tab) => {
        const active = tab.to === '/sales' ? pathname === '/sales' : pathname.startsWith(tab.to);
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={cn(
              'mono-label relative pb-mx-2 pt-mx-1 text-mx-grey-mid hover:text-mx-white',
              active && 'text-mx-white',
            )}
          >
            {tab.label}
            {active && (
              <span
                aria-hidden
                className="absolute -bottom-[1px] left-0 right-0 h-[2px] bg-mx-orange"
              />
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function SalesLayout() {
  return (
    <>
      <PageHeader eyebrowNumber="02" eyebrowText="Sales" title="Sales" tabs={<TabStrip />} />
      <Outlet />
    </>
  );
}
